import React, { useCallback, useEffect, useState } from "react";
import TopBar from "../../components/TopBar";
import { getInvoices, InvoicesResponse } from "../invoices/invoicesService";
import {
  CollectionStatus,
  IntegratorPaymentOverview,
  PaymentMethodType,
  PaymentMethodsResponse,
  SafePaymentMethod,
  confirmPaymentMethodSetup,
  createPaymentMethodSetup,
  getPaymentMethods,
  redirectToStripe,
} from "./paymentMethodsService";
import "./PaymentMethods.css";

type Props = { user?: any; onSignOut?: () => void };

function emailOf(user: any) {
  return (
    user?.signInDetails?.loginId ||
    user?.attributes?.email ||
    user?.username ||
    ""
  );
}

function money(value: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function number(value: number) {
  return new Intl.NumberFormat("en-US").format(Number(value || 0));
}

function cardBrand(value?: string | null) {
  const brand = String(value || "Card").trim();
  return brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card";
}

function paymentMethodName(method?: SafePaymentMethod) {
  return method?.type === "ACSS_DEBIT"
    ? method.bankName || "Canadian bank"
    : cardBrand(method?.brand);
}

function paymentMethodDetails(method?: SafePaymentMethod) {
  if (method?.type === "ACSS_DEBIT") {
    return `${method.currency || "CAD"} pre-authorized debit`;
  }
  return `Expires ${String(method?.expMonth || "--").padStart(2, "0")}/${String(method?.expYear || "--").slice(-2)}`;
}

function statusLabel(status?: CollectionStatus) {
  if (status === "PAID") return "Paid";
  if (status === "PROCESSING") return "Processing";
  if (status === "FAILED") return "Payment failed";
  if (status === "ACTION_REQUIRED") return "Action required";
  if (status === "READY") return "Automatic";
  return "Not configured";
}

function Metric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <article className="payment-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function PaymentCard({
  method,
  title,
  description,
  busy,
  currency,
  onSetup,
}: {
  method?: SafePaymentMethod;
  title: string;
  description: string;
  busy: PaymentMethodType | null;
  currency: "CAD" | "USD";
  onSetup: (type: PaymentMethodType) => void;
}) {
  const configured = method?.configured === true;
  const bankConfigured = configured && method?.type === "ACSS_DEBIT";
  const actions = (
    <div className="payment-method-actions">
      <button
        type="button"
        className={bankConfigured ? "payment-secondary" : "payment-primary"}
        disabled={busy !== null}
        onClick={() => onSetup("ACSS_DEBIT")}
      >
        {busy === "ACSS_DEBIT"
          ? "Opening Stripe…"
          : bankConfigured
            ? "Replace bank account"
            : "Use bank account — Recommended"}
      </button>
      <button
        type="button"
        className="payment-secondary"
        disabled={busy !== null}
        onClick={() => onSetup("CARD")}
      >
        {busy === "CARD"
          ? "Opening Stripe…"
          : configured && !bankConfigured
            ? "Replace card"
            : "Use credit card"}
      </button>
    </div>
  );
  return (
    <section className="payment-panel">
      <div className="payment-panel-heading">
        <div>
          <span className="payment-eyebrow">Automatic billing</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <span className={`payment-state payment-state--${configured ? "ready" : "attention"}`}>
          {statusLabel(method?.status)}
        </span>
      </div>
      {configured ? (
        <div className="payment-card-row">
          <span className={`payment-card-brand${bankConfigured ? " payment-card-brand--bank" : ""}`}>
            {bankConfigured ? "BANK" : cardBrand(method?.brand)}
          </span>
          <div>
            <strong>{paymentMethodName(method)} •••• {method?.last4 || "••••"}</strong>
            <small>{paymentMethodDetails(method)}</small>
          </div>
          {actions}
        </div>
      ) : (
        <div className="payment-empty-method">
          <div>
            <strong>No payment method</strong>
            <span>Connect a Canadian bank account for lower fees, or keep a card as a backup.</span>
          </div>
          {actions}
        </div>
      )}
      <p className="payment-security-note">
        Bank debit is authorized for variable monthly {currency} invoices. Qumra stores only the payment type, bank or card label, and last four digits; sensitive details and the mandate remain with Stripe.
      </p>
    </section>
  );
}

function OwnerIntegratorTable({ items }: { items: IntegratorPaymentOverview[] }) {
  if (!items.length) {
    return <div className="payment-empty">No Integrator payment profiles are available yet.</div>;
  }
  return (
    <div className="payment-table-wrap">
      <table className="payment-table">
        <thead>
          <tr><th>Integrator</th><th>Payment method</th><th>Collection</th></tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.partnerId}>
              <td><strong>{item.displayName}</strong>{item.email && <small>{item.email}</small>}</td>
              <td>{item.paymentMethod.configured ? <><strong>{paymentMethodName(item.paymentMethod)} •••• {item.paymentMethod.last4}</strong><small>{paymentMethodDetails(item.paymentMethod)}</small></> : <span className="payment-muted">Not configured</span>}</td>
              <td><span className={`payment-state payment-state--${item.paymentMethod.configured ? "ready" : "attention"}`}>{statusLabel(item.paymentMethod.status)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PaymentMethods({ user, onSignOut }: Props) {
  const [data, setData] = useState<PaymentMethodsResponse | null>(null);
  const [invoices, setInvoices] = useState<InvoicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<PaymentMethodType | "CONFIRM" | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [paymentsResult, invoicesResult] = await Promise.allSettled([
        getPaymentMethods(),
        getInvoices(),
      ]);
      if (paymentsResult.status === "rejected") throw paymentsResult.reason;
      setData(paymentsResult.value);
      setInvoices(invoicesResult.status === "fulfilled" ? invoicesResult.value : null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load payment methods.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("setup") === "success" && sessionId) {
      setBusy("CONFIRM");
      void confirmPaymentMethodSetup(sessionId)
        .then((result) => {
          if (!active) return;
          setMessage(result.paymentMethod.type === "ACSS_DEBIT"
            ? "Your Canadian bank account is authorized for automatic billing. Bank payments will show as Processing until they clear."
            : "Your card is ready for automatic billing.");
          window.history.replaceState({}, "", window.location.pathname);
        })
        .catch((caught) => active && setError(caught instanceof Error ? caught.message : "Unable to confirm the payment method."))
        .finally(() => {
          if (active) {
            setBusy(null);
            void load();
          }
        });
      return () => { active = false; };
    }
    if (params.get("setup") === "cancelled") {
      setMessage("Payment setup was cancelled. No payment method was changed.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    void load();
    return () => { active = false; };
  }, [load]);

  async function setupPaymentMethod(paymentMethodType: PaymentMethodType) {
    setBusy(paymentMethodType);
    setError("");
    try {
      const invoiceCurrency = String(invoices?.currency || "CAD").toUpperCase();
      const paymentCurrency: "CAD" | "USD" = invoiceCurrency === "USD" ? "USD" : "CAD";
      const result = await createPaymentMethodSetup(paymentMethodType, paymentCurrency);
      redirectToStripe(result.url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to open Stripe payment setup.");
      setBusy(null);
    }
  }

  const role = data?.role || "INTEGRATOR";
  const summary = invoices?.summary;
  const currency = invoices?.currency || "USD";
  const paymentCurrency: "CAD" | "USD" = String(currency).toUpperCase() === "USD" ? "USD" : "CAD";

  return (
    <div className="payments-page">
      <TopBar title="Qumra" email={emailOf(user)} onSignOut={onSignOut} />
      <main className="payments-shell">
        <header className="payments-hero">
          <div><span className="payment-eyebrow">Secure billing</span><h1>Payment methods</h1><p>{role === "INTEGRATOR" ? "Pay Qumra wholesale invoices for every camera in your Integrator account." : "Monitor Integrator payment readiness and wholesale collection."}</p></div>
          <span className="payments-role">{role}</span>
        </header>

        {error && <div className="payment-alert payment-alert--error" role="alert"><span>{error}</span><button type="button" onClick={() => void load()}>Try again</button></div>}
        {message && <div className="payment-alert payment-alert--success" role="status">{message}</div>}
        {data && !data.collectionEnabled && <div className="payment-alert payment-alert--info">Bank accounts and cards can be added safely now. Live automatic collection remains off until the Owner enables Stripe collection after test-mode verification.</div>}

        {loading && !data ? <div className="payment-loading">Loading secure payment settings…</div> : data && (
          <>
            <section className="payment-metrics" aria-label="Payment summary">
              {role === "INTEGRATOR" ? <>
                <Metric label="Due to Qumra" value={money(summary?.wholesaleTotal || 0, currency)} detail="Private wholesale estimate" />
                <Metric label="Cameras billed" value={number(summary?.cameraCount || 0)} detail="All cameras owned by your account" />
                <Metric label="AI Monitoring" value={money(summary?.wholesaleAiMonitoringTotal || 0, currency)} detail="Wholesale token usage" />
              </> : <>
                <Metric label="Wholesale receivable" value={money(summary?.wholesaleTotal || 0, currency)} detail="Across all open camera cycles" />
                <Metric label="Integrators" value={number(data.integrators?.length || 0)} detail="Payment profiles in your tenant" />
                <Metric label="Ready for collection" value={number((data.integrators || []).filter((item) => item.paymentMethod.configured).length)} detail="Integrator payment methods configured" />
              </>}
            </section>

            {role === "INTEGRATOR" && <PaymentCard method={data.paymentMethod} title="Pay Qumra" description="Your preferred payment method pays one consolidated wholesale invoice for all cameras in your Integrator account." busy={busy === "CARD" || busy === "ACSS_DEBIT" ? busy : null} currency={paymentCurrency} onSetup={(type) => void setupPaymentMethod(type)} />}
            {role === "OWNER" && <section className="payment-panel"><div className="payment-panel-heading"><div><span className="payment-eyebrow">Owner operations</span><h2>Integrator payment readiness</h2><p>Only safe payment summaries are displayed. Bank mandates and full payment details remain with Stripe.</p></div></div><OwnerIntegratorTable items={data.integrators || []} /></section>}
          </>
        )}
      </main>
    </div>
  );
}
